import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';
import { PlatformEnum } from 'src/app/shared/constants/platform-type.enum';
import {
  ImageOrderingResult,
  SortColumn,
  SortDirection,
} from '@shared/models/ai-agent-response';
import { SimpleChanges } from '@angular/core';
import { OnChanges } from '@angular/core';
export interface DetectedProduct {
  rowNumber: number;
  productNumber: string;
  productName: string;
  detectedQuantity: number;
  quantityType: string;
  quantityCases: number;
  quantityEaches: number;
  matchedProductNumber: string;
  isEditable: boolean;
  matchDescription: string;
}

interface ProductGroup {
  fileName: string;
  imageId: string;
  products: DetectedProduct[];
}

@Component({
  selector: 'app-image-ordering-table',
  templateUrl: './image-ordering-table.component.html',
  styleUrls: ['./image-ordering-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageOrderingTableComponent implements OnInit, OnChanges {
  @Input() imageOrderingResults: ImageOrderingResult[] = [];
  @Input() platform: PlatformEnum;

  @Output() addToCartEmitter = new EventEmitter<void>();
  @Output() addMoreItemsEmitter = new EventEmitter<void>();
  @Output() deleteProductEmitter = new EventEmitter<{
    imageId: string;
    matchedProductNumber: string;
    quantityCases: number;
    quantityEaches: number;
  }>();
  @Output() quantityChangeEmitter = new EventEmitter<{
    imageId: string;
    matchedProductNumber: string;
    newQuantityCases: number;
    newQuantityEaches: number;
  }>();

  readonly PlatformEnum = PlatformEnum;
  readonly SortColumn = SortColumn;
  readonly SortDirection = SortDirection;

  productGroups: ProductGroup[] = [];
  currentSortColumn: SortColumn | null = null;
  currentSortDirection: SortDirection = SortDirection.ASC;

  ngOnInit() {
    this.transformResultsToDetectedProducts();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['imageOrderingResults']) {
      this.transformResultsToDetectedProducts();
    }
  }

  private transformResultsToDetectedProducts() {
    this.productGroups = [];

    this.imageOrderingResults.forEach(result => {
      let rowNumber = 1;
      const products: DetectedProduct[] = result.matches.map(match => ({
        rowNumber: rowNumber++,
        productNumber: match.productNumber?.toString() || '',
        productName: match.productDescription,
        quantityCases: match.quantityCases || 0,
        quantityEaches: match.quantityEaches || 0,
        detectedQuantity: match.quantityCases
          ? match.quantityCases
          : match.quantityEaches
            ? match.quantityEaches
            : 0,
        quantityType: match.quantityCases
          ? 'CS'
          : match.quantityEaches
            ? 'EA'
            : '',
        matchedProductNumber: match.matchedProductNumber?.toString() || '',
        matchDescription: this.getMatchDescription(match),
        isEditable: !!match.matchedProductNumber,
      }));

      this.productGroups.push({
        fileName: result.fileName,
        imageId: result.imageId,
        products,
      });
    });

    this.applySorting();
  }

  private getMatchDescription(match: any): string {
    const hasMatchedProductNumber = !!match.matchedProductNumber;
    const hasProductNumber = !!match.productNumber;

    if (hasMatchedProductNumber) {
      return match.matchedProductNumber + ' - ' + match.productDescription;
    } else if (hasProductNumber) {
      return 'Not Previously Purchased';
    } else {
      return 'Not Found';
    }
  }

  handleSort(column: SortColumn): void {
    if (this.currentSortColumn === column) {
      this.currentSortDirection =
        this.currentSortDirection === SortDirection.ASC
          ? SortDirection.DESC
          : SortDirection.ASC;
    } else {
      this.currentSortColumn = column;
      this.currentSortDirection = SortDirection.DESC;
    }

    this.applySorting();
  }

  private applySorting(): void {
    if (!this.currentSortColumn) {
      return;
    }

    this.productGroups.forEach(group => {
      group.products.sort((a, b) => {
        let aValue: any = a[this.currentSortColumn];
        let bValue: any = b[this.currentSortColumn];

        if (
          this.currentSortColumn === SortColumn.ROW_NUMBER ||
          this.currentSortColumn === SortColumn.DETECTED_QUANTITY ||
          this.currentSortColumn === SortColumn.PRODUCT_NUMBER
        ) {
          aValue =
            this.currentSortColumn === SortColumn.PRODUCT_NUMBER
              ? parseInt(aValue) || 0
              : aValue;
          bValue =
            this.currentSortColumn === SortColumn.PRODUCT_NUMBER
              ? parseInt(bValue) || 0
              : bValue;
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }

        if (aValue < bValue)
          return this.currentSortDirection === SortDirection.ASC ? -1 : 1;
        if (aValue > bValue)
          return this.currentSortDirection === SortDirection.ASC ? 1 : -1;
        return 0;
      });
    });
  }

  getSortDirection(column: SortColumn): SortDirection {
    return this.currentSortColumn === column
      ? this.currentSortDirection
      : SortDirection.NONE;
  }

  isSortActive(column: SortColumn): boolean {
    return this.currentSortColumn === column;
  }

  handleAddToCart(): void {
    this.addToCartEmitter.emit();
  }

  handleAddMoreItems(): void {
    this.addMoreItemsEmitter.emit();
  }

  handleDeleteProduct(imageId: string, matchedProductNumber: string): void {
    const group = this.productGroups.find(group => group.imageId === imageId);
    const product = group.products.find(
      product => product.matchedProductNumber === matchedProductNumber,
    );

    this.deleteProductEmitter.emit({
      imageId,
      matchedProductNumber: product.matchedProductNumber,
      quantityCases: product.quantityCases,
      quantityEaches: product.quantityEaches,
    });
  }

  handleQuantityChange(
    imageId: string,
    matchedProductNumber: string,
    newQuantity: number,
    quantityType: string,
  ): void {
    this.quantityChangeEmitter.emit({
      imageId,
      matchedProductNumber,
      newQuantityCases: quantityType === 'CS' ? newQuantity : 0,
      newQuantityEaches: quantityType === 'EA' ? newQuantity : 0,
    });
  }

  trackByRowNumber(index: number, item: DetectedProduct): number {
    return item.rowNumber;
  }

  trackByGroupId(index: number, item: ProductGroup): string {
    return `${item.fileName}-${item.imageId}`;
  }

  getExpandedAccordions(): string[] {
    return this.productGroups.map(
      group => `${group.fileName}-${group.imageId}`,
    );
  }
}
